import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ActiveEmployerCompanyService } from '../../../services/active-employer-company/active-employer-company.service';
import { TeamsService } from '../../../services/teams/teams.service';
import { TeamCompaniesService } from '../../../services/teamcompanies/teamcompanies.service';
import { UsersService } from '../../../services/users/users.service';
import {
  PayrollProviderConnectionResponse,
  PayrollService,
  UkgDeductionCode,
} from '../../../services/payroll/payroll.service';
import { ManagedFeatureFlagsService } from '../../../services/managed-feature-flags/managed-feature-flags.service';
import { SessionService } from '../../../services/session/session.service';
import { PayrollAccessService } from '../../../services/payroll-access/payroll-access.service';
import {
  BenefitProductItem,
  PayrollBenefitsAssignmentService,
} from '../../../services/payroll-benefits-assignment/payroll-benefits-assignment.service';
import {
  PayrollSnackRewardItem,
  PayrollSnacksAssignmentService,
} from '../../../services/payroll-snacks-assignment/payroll-snacks-assignment.service';
import { Subscription } from 'rxjs';
import {
  BenefitCategoryTab,
  buildPayrollBenefitSectionDefinitions,
  PayrollBenefitSectionDefinition,
  PayrollBenefitSubcategoryDefinitionsByCategory,
} from '../../../services/payroll-benefits-assignment/payroll-benefit-categories';
import {
  GroupProductOnboardingRequestDialogComponent,
  GroupProductOnboardingRequestDialogResult,
} from '../group-product-onboarding-request-dialog/group-product-onboarding-request-dialog.component';
import { SnackBarComponent } from '../../snackbar/snackbar.component';
import {
  PAYROLL_ADMIN_COMPANY_SNACK_REWARD_SELECTION_FLAG,
  PAYROLL_ADMIN_PRODUCT_PAYROLL_DEDUCTION_FLAG,
} from '../payroll-admin-feature-flags';

interface BenefitSection {
  id: string;
  title: string;
  products: BenefitProductItem[];
}

interface TeamRow {
  id: string;
  name: string;
  membersCount: number;
  metadata: any;
}

type PayrollBenefitsTopTab = 'benefits' | 'snacks' | 'contributions';

@Component({
    selector: 'app-payroll-products',
    templateUrl: './payroll-products.component.html',
    styleUrls: ['./payroll-products.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PayrollProductsComponent implements OnInit, OnChanges, OnDestroy {
  private readonly alphabeticalCollator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

  @Input() teamId: string | undefined;
  @Input() companyId = '';

  protected isInitialCompanyCheckComplete = false;
  protected isLoading = false;
  protected isSaving = false;
  protected isLoadingSnackRewards = false;
  protected isLoadingPayrollDeductionCodes = false;
  protected isPayrollDeductionFeatureEnabled = false;
  protected isCompanySnackRewardSelectionEnabled = false;
  protected hasCompany = false;
  protected isNativeCompanyManager = false;

  protected company: any;
  protected teams: TeamRow[] = [];
  protected activeTopTab: PayrollBenefitsTopTab = 'benefits';

  protected benefitProducts: BenefitProductItem[] = [];
  protected payrollDeductionCodes: UkgDeductionCode[] = [];
  protected payrollDeductionCodesLoadError = '';
  protected snackRewards: PayrollSnackRewardItem[] = [];
  protected previewSnackRewardId = '';
  protected selectedBenefitProductIds = new Set<string>();
  protected selectedSnackRewardIds = new Set<string>();
  protected selectedTeamIds = new Set<string>();
  protected pendingOnboardingRequestProductIds = new Set<string>();
  protected failedBenefitProductImageIds = new Set<string>();
  protected retryingBenefitProductImageIds = new Set<string>();

  private companyProductIdsInOrder: string[] = [];
  private companyBaselineSelectedProductIds: string[] = [];
  private companySnackRewardIdsInOrder: string[] = [];
  private currentUserReady = false;
  private meSubscription?: Subscription;
  private activeCompanySubscription?: Subscription;
  private loadedActiveCompanyId = '';
  private initialProductPayrollDeductionMapSnapshot = '';
  private hasAttemptedPayrollDeductionCodesLoad = false;
  private payrollProviderConnection: PayrollProviderConnectionResponse | null = null;
  private payrollDeductionCodesRequest?: Promise<void>;
  private payrollDeductionFeatureFlagRequest?: Promise<void>;
  private companySnackRewardSelectionFeatureFlagRequest?: Promise<void>;
  private benefitProductImageRetryAttempts = new Map<string, number>();
  private benefitProductImageRetryTimers = new Map<string, number>();
  private readonly benefitProductImageMaxRetryAttempts = 3;

  protected readonly categories: Array<{ id: BenefitCategoryTab; title: string }> = [
    { id: 'health', title: 'Health' },
    { id: 'supplemental', title: 'Supplemental' },
    { id: 'retirement', title: 'Retirement' },
    { id: 'pnc', title: 'P&C' },
    { id: 'other', title: 'Other' },
  ];

  protected subcategoryDefinitionsByCategory: PayrollBenefitSubcategoryDefinitionsByCategory = {};

  constructor(
    private teamsService: TeamsService,
    private activeEmployerCompanyService: ActiveEmployerCompanyService,
    private usersService: UsersService,
    private payrollBenefitsAssignmentService: PayrollBenefitsAssignmentService,
    private payrollSnacksAssignmentService: PayrollSnacksAssignmentService,
    private teamCompaniesService: TeamCompaniesService,
    private payrollService: PayrollService,
    private managedFeatureFlagsService: ManagedFeatureFlagsService,
    private sessionService: SessionService,
    private payrollAccessService: PayrollAccessService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.meSubscription = this.usersService.meSubscribe(async (me: any) => {
      if (!me) {
        return;
      }
      this.currentUserReady = true;
      this.isNativeCompanyManager = this.payrollAccessService.workspaceContext === 'nativeCompanyManager';
      if (this.isNativeCompanyManager) {
        const companyState = await this.activeEmployerCompanyService.initialize();
        this.company = companyState.activeCompany;
        this.companyId = this.company?.id || '';
        this.hasCompany = !!this.companyId;
        this.isInitialCompanyCheckComplete = true;
      } else {
        const cachedCompanies = this.usersService.getCachedTeamCompanies();
        const providedCompanyId = `${this.companyId || ''}`.trim();
        if (providedCompanyId) {
          this.hasCompany = true;
          this.isInitialCompanyCheckComplete = true;
        } else if (cachedCompanies) {
          this.hasCompany = cachedCompanies.length > 0;
          this.company = cachedCompanies[0];
          this.companyId = this.company?.id || '';
          this.isInitialCompanyCheckComplete = true;
        }
      }
      try {
        await this.loadData();
      } finally {
        this.isInitialCompanyCheckComplete = true;
      }
      this.subscribeToActiveCompanyChanges();
    });
  }

  ngOnDestroy(): void {
    this.meSubscription?.unsubscribe();
    this.activeCompanySubscription?.unsubscribe();
    this.clearBenefitProductImageRetryTimers();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.currentUserReady) {
      return;
    }
    if (changes.teamId || changes.companyId) {
      await this.loadData();
    }
  }

  protected get selectedBenefitsCount(): number {
    return this.selectedBenefitProductIds.size;
  }

  protected get selectedSnacksCount(): number {
    return this.selectedSnackRewardIds.size;
  }

  protected get selectedTeamsCount(): number {
    return this.selectedTeamIds.size;
  }

  protected get canAssign(): boolean {
    return !this.isSaving && !this.isLoadingPayrollDeductionCodes;
  }

  protected get canAssignSnacks(): boolean {
    return !this.isSaving && !this.isLoadingSnackRewards;
  }

  protected get hasSelections(): boolean {
    return this.selectedBenefitProductIds.size > 0 || this.selectedTeamIds.size > 0;
  }

  protected get isAllTeamsSelected(): boolean {
    return this.teams.length > 0 && this.selectedTeamIds.size === this.teams.length;
  }

  protected get isSomeTeamsSelected(): boolean {
    return this.selectedTeamIds.size > 0 && !this.isAllTeamsSelected;
  }

  protected get isAllBenefitsSelected(): boolean {
    const selectableIds = this.getSelectableBenefitProductIds();
    return selectableIds.length > 0 && selectableIds.every((productId) => this.selectedBenefitProductIds.has(productId));
  }

  protected get isSomeBenefitsSelected(): boolean {
    return this.getSelectableBenefitProductIds().some((productId) => this.selectedBenefitProductIds.has(productId))
      && !this.isAllBenefitsSelected;
  }

  protected get isAllSnacksSelected(): boolean {
    return this.snackRewards.length > 0
      && this.snackRewards.every((reward) => this.selectedSnackRewardIds.has(reward.id));
  }

  protected get isSomeSnacksSelected(): boolean {
    return this.snackRewards.some((reward) => this.selectedSnackRewardIds.has(reward.id))
      && !this.isAllSnacksSelected;
  }

  protected isBenefitProductSelected(productId: string): boolean {
    return this.selectedBenefitProductIds.has(productId);
  }

  protected isSnackRewardSelected(rewardId: string): boolean {
    return this.selectedSnackRewardIds.has(rewardId);
  }

  protected isSnackRewardPreviewed(rewardId: string): boolean {
    return !!rewardId && this.getPreviewSnackReward()?.id === rewardId;
  }

  protected isBenefitProductPending(product: BenefitProductItem): boolean {
    return product?.approvalStatus === 'pending';
  }

  protected isBenefitProductRequestable(product: BenefitProductItem): boolean {
    return !this.isNativeCompanyManager
      && product?.requiresApproval === true
      && product?.canRequestApproval === true;
  }

  protected get showSnacksSection(): boolean {
    return this.isNativeCompanyManager
      ? this.snackRewards.length > 0
      : this.isCompanySnackRewardSelectionEnabled;
  }

  protected canToggleBenefitProduct(product: BenefitProductItem): boolean {
    return !this.isSaving
      && product?.alwaysActive !== true
      && !this.pendingOnboardingRequestProductIds.has(product.id)
      && !this.isBenefitProductPending(product);
  }

  protected canInteractWithBenefitProductCheckbox(product: BenefitProductItem): boolean {
    return this.canToggleBenefitProduct(product) && !this.isBenefitProductRequestable(product);
  }

  protected get isPayrollDeductionAvailable(): boolean {
    return this.isPayrollDeductionFeatureEnabled
      && this.payrollService.providerConnectionSupportsCapability(
        this.payrollProviderConnection,
        'supportsPayrollDeductionSetup',
      );
  }

  protected canEditPayrollDeduction(): boolean {
    return !this.isSaving && this.isPayrollDeductionAvailable;
  }

  protected async onPayrollDeductionToggle(product: BenefitProductItem, checked: boolean): Promise<void> {
    if (!this.canEditPayrollDeduction()) {
      return;
    }

    product.payrollDeductionEnabled = checked;
    if (!checked) {
      product.payrollDeductionCode = '';
      product.payrollDeductionCodeDescription = '';
      return;
    }

    await this.ensurePayrollDeductionCodesLoaded();
  }

  protected onPayrollDeductionCodeSelected(product: BenefitProductItem, deductionCode: string): void {
    const normalizedDeductionCode = `${deductionCode || ''}`.trim();
    const matchedCode = this.payrollDeductionCodes.find((option) => option.code === normalizedDeductionCode);
    product.payrollDeductionCode = normalizedDeductionCode;
    product.payrollDeductionCodeDescription = matchedCode?.description || '';
  }

  protected async retryPayrollDeductionCodesLoad(): Promise<void> {
    await this.ensurePayrollDeductionCodesLoaded(true);
  }

  protected getPayrollDeductionValidationMessage(product: BenefitProductItem): string {
    if (product?.payrollDeductionEnabled !== true || !this.isPayrollDeductionAvailable) {
      return '';
    }

    if (this.isLoadingPayrollDeductionCodes) {
      return 'Loading deduction codes from UKG Ready...';
    }

    if (this.payrollDeductionCodesLoadError) {
      return `${this.payrollDeductionCodesLoadError} Add or manage the deduction code in UKG Ready, then retry.`;
    }

    if (this.hasAttemptedPayrollDeductionCodesLoad && this.payrollDeductionCodes.length === 0) {
      return 'No deduction codes are available from UKG Ready yet. Add the code in UKG Ready, then retry.';
    }

    const selectedCode = `${product?.payrollDeductionCode || ''}`.trim();
    if (!selectedCode) {
      return 'Select a deduction code before saving. If the desired deduction code is not listed, add it in UKG Ready.';
    }

    if (!this.payrollDeductionCodes.some((option) => option.code === selectedCode)) {
      return 'This deduction code is not currently available from UKG Ready. Add or manage it in UKG Ready, then retry.';
    }

    return '';
  }

  protected onBenefitProductToggle(productId: string, checked: boolean): void {
    const next = new Set(this.selectedBenefitProductIds);
    if (checked) {
      next.add(productId);
    } else {
      next.delete(productId);
    }
    this.selectedBenefitProductIds = next;
  }

  protected onSnackRewardToggle(rewardId: string, checked: boolean): void {
    const next = new Set(this.selectedSnackRewardIds);
    if (checked) {
      next.add(rewardId);
    } else {
      next.delete(rewardId);
    }
    this.selectedSnackRewardIds = next;
  }

  protected toggleBenefitProduct(productId: string): void {
    this.onBenefitProductToggle(productId, !this.isBenefitProductSelected(productId));
  }

  protected toggleSnackReward(rewardId: string): void {
    this.onSnackRewardToggle(rewardId, !this.isSnackRewardSelected(rewardId));
  }

  protected previewSnackReward(reward: PayrollSnackRewardItem, event?: Event): void {
    event?.stopPropagation();
    const rewardId = reward?.id || '';
    this.previewSnackRewardId = this.previewSnackRewardId === rewardId ? '' : rewardId;
  }

  protected getPreviewSnackReward(): PayrollSnackRewardItem | undefined {
    if (!this.previewSnackRewardId) {
      return undefined;
    }
    return this.snackRewards.find((reward) => reward.id === this.previewSnackRewardId);
  }

  protected async onBenefitProductClicked(product: BenefitProductItem): Promise<void> {
    if (this.isSaving || this.pendingOnboardingRequestProductIds.has(product.id)) {
      return;
    }
    if (product?.alwaysActive === true) {
      return;
    }
    if (this.isBenefitProductPending(product)) {
      return;
    }
    if (this.isBenefitProductRequestable(product)) {
      await this.requestGroupProductOnboarding(product);
      return;
    }
    this.toggleBenefitProduct(product.id);
  }

  protected async onBenefitProductCheckboxChanged(
    product: BenefitProductItem,
    checked: boolean,
  ): Promise<void> {
    if (this.isSaving || this.pendingOnboardingRequestProductIds.has(product.id)) {
      return;
    }
    if (product?.alwaysActive === true) {
      return;
    }
    if (this.isBenefitProductRequestable(product)) {
      return;
    }
    if (this.isBenefitProductPending(product)) {
      return;
    }
    this.onBenefitProductToggle(product.id, checked);
  }

  protected openBenefitProductDetails(product: BenefitProductItem, event: Event): void {
    event.stopPropagation();
    const normalizedUrl = `${product?.detailsUrl || ''}`.trim();
    if (!normalizedUrl) {
      return;
    }
    window.open(normalizedUrl, '_blank', 'noopener');
  }

  protected onToggleAllBenefits(change: MatCheckboxChange): void {
    if (!change.checked) {
      this.selectedBenefitProductIds = new Set(this.getAlwaysActiveBenefitProductIds());
      return;
    }
    this.selectedBenefitProductIds = new Set([
      ...this.getAlwaysActiveBenefitProductIds(),
      ...this.getSelectableBenefitProductIds(),
    ]);
  }

  protected onToggleAllSnacks(change: MatCheckboxChange): void {
    this.selectedSnackRewardIds = change.checked
      ? new Set(this.snackRewards.map((reward) => reward.id))
      : new Set<string>();
  }

  protected isTeamSelected(teamId: string): boolean {
    return this.selectedTeamIds.has(teamId);
  }

  protected onTeamToggle(teamId: string, checked: boolean): void {
    const next = new Set(this.selectedTeamIds);
    if (checked) {
      next.add(teamId);
    } else {
      next.delete(teamId);
    }
    this.selectedTeamIds = next;
  }

  protected toggleTeam(teamId: string): void {
    this.onTeamToggle(teamId, !this.isTeamSelected(teamId));
  }

  protected onToggleAllTeams(change: MatCheckboxChange): void {
    if (!change.checked) {
      this.selectedTeamIds = new Set<string>();
      return;
    }
    this.selectedTeamIds = new Set(this.teams.map(team => team.id));
  }

  protected setTopTab(tab: PayrollBenefitsTopTab): void {
    if (tab === 'snacks' && !this.showSnacksSection) {
      return;
    }
    if (tab === 'contributions' && this.isNativeCompanyManager) {
      return;
    }
    this.activeTopTab = tab;
  }

  protected clearSelections(): void {
    this.selectedBenefitProductIds = new Set(this.getAlwaysActiveBenefitProductIds());
    this.selectedTeamIds = new Set<string>();
  }

  protected clearSnackSelections(): void {
    this.selectedSnackRewardIds = new Set<string>();
    this.selectedTeamIds = new Set<string>();
  }

  protected loadBenefitSelectionsFromTeam(team: TeamRow): void {
    const availableIds = new Set(this.benefitProducts.map((product) => product.id));
    const selectedIds = this.payrollBenefitsAssignmentService.getSelectedMappedBenefitProductIds(
      this.company,
      team
    ).filter((productId) => availableIds.has(productId));
    this.selectedBenefitProductIds = new Set(selectedIds);

    const successMessage = selectedIds.length
      ? `Loaded ${selectedIds.length} benefit${selectedIds.length === 1 ? '' : 's'} from ${team.name}.`
      : `Loaded no benefits from ${team.name}.`;
    this.snackBar.open(successMessage, 'Close', {
      duration: 3000,
    });
  }

  protected loadSnackSelectionsFromTeam(team: TeamRow): void {
    const selectedIds = this.payrollSnacksAssignmentService.getSelectedSnackRewardIds(
      this.companySnackRewardIdsInOrder,
      team,
    );
    this.selectedSnackRewardIds = new Set(selectedIds);

    const successMessage = selectedIds.length
      ? `Loaded ${selectedIds.length} snack${selectedIds.length === 1 ? '' : 's'} from ${team.name}.`
      : `Loaded no snacks from ${team.name}.`;
    this.snackBar.open(successMessage, 'Close', {
      duration: 3000,
    });
  }

  protected getBenefitSections(category: BenefitCategoryTab): BenefitSection[] {
    const products = this.benefitProducts.filter(product => product.mapping.category === category);
    const sectionDefs: PayrollBenefitSectionDefinition[] = buildPayrollBenefitSectionDefinitions(
      category,
      this.subcategoryDefinitionsByCategory,
      products.map((product) => product.mapping)
    );

    const sections: BenefitSection[] = sectionDefs.map(section => ({
      id: section.id,
      title: section.title,
      products: section.subcategory
        ? this.sortBenefitProducts(products.filter(product => product.mapping.subcategory === section.subcategory))
        : this.sortBenefitProducts([...products]),
    }));

    const assignedIds = new Set(
      sections.reduce((all: string[], section: BenefitSection) => {
        return all.concat(section.products.map((product: BenefitProductItem) => product.id));
      }, [])
    );
    const uncategorizedProducts = products.filter(product => !assignedIds.has(product.id));
    if (uncategorizedProducts.length > 0 || sections.length === 0) {
      sections.push({
        id: 'general',
        title: 'General',
        products: this.sortBenefitProducts(uncategorizedProducts),
      });
    }

    return sections;
  }

  private compareAlphabetically(left: string | undefined | null, right: string | undefined | null): number {
    return this.alphabeticalCollator.compare((left || '').trim(), (right || '').trim());
  }

  private sortBenefitProducts(products: BenefitProductItem[]): BenefitProductItem[] {
    return [...products].sort((a, b) => {
      const primaryComparison = this.compareAlphabetically(
        a.vendorName || a.productName,
        b.vendorName || b.productName
      );
      if (primaryComparison !== 0) {
        return primaryComparison;
      }

      const secondaryComparison = this.compareAlphabetically(a.productName, b.productName);
      if (secondaryComparison !== 0) {
        return secondaryComparison;
      }

      return this.compareAlphabetically(a.description, b.description);
    });
  }

  protected getPopulatedBenefitSections(category: BenefitCategoryTab): BenefitSection[] {
    return this.getBenefitSections(category).filter(section => section.products.length > 0);
  }

  protected trackCategory(index: number, category: { id: BenefitCategoryTab; title: string }): string {
    return category.id;
  }

  protected trackSection(index: number, section: BenefitSection): string {
    return section.id;
  }

  protected trackProduct(index: number, product: BenefitProductItem): string {
    return product.id;
  }

  protected trackSnackReward(index: number, reward: PayrollSnackRewardItem): string {
    return reward.id;
  }

  protected trackTeam(index: number, team: TeamRow): string {
    return team.id;
  }

  protected getBenefitProductStatusLabel(product: BenefitProductItem): string {
    if (this.pendingOnboardingRequestProductIds.has(product.id)) {
      return 'Sending request...';
    }
    return product?.approvalLabel || '';
  }

  protected getBenefitProductStatusClass(product: BenefitProductItem): string {
    if (this.pendingOnboardingRequestProductIds.has(product.id)) {
      return 'status-pending';
    }

    const label = `${product?.approvalLabel || ''}`.trim().toLowerCase();
    if (label === 'ready') {
      return 'status-available';
    }

    if (label) {
      return 'status-action-needed';
    }

    return '';
  }

  protected shouldShowBenefitProductImage(product: BenefitProductItem): boolean {
    return !!product?.imageUrl
      && !this.failedBenefitProductImageIds.has(product.id)
      && !this.retryingBenefitProductImageIds.has(product.id);
  }

  protected shouldShowBenefitProductImagePlaceholder(product: BenefitProductItem): boolean {
    if (!product?.imageUrl) {
      return true;
    }
    return this.failedBenefitProductImageIds.has(product.id)
      || this.retryingBenefitProductImageIds.has(product.id);
  }

  protected onBenefitProductImageError(product: BenefitProductItem): void {
    if (!product?.id) {
      return;
    }

    const attempts = this.benefitProductImageRetryAttempts.get(product.id) || 0;
    if (attempts < this.benefitProductImageMaxRetryAttempts) {
      this.benefitProductImageRetryAttempts.set(product.id, attempts + 1);
      this.setBenefitProductImageRetrying(product.id, true);
      this.scheduleBenefitProductImageRetry(product.id, attempts + 1);
      return;
    }

    const nextIds = new Set(this.failedBenefitProductImageIds);
    nextIds.add(product.id);
    this.failedBenefitProductImageIds = nextIds;
    this.setBenefitProductImageRetrying(product.id, false);
  }

  protected onBenefitProductImageLoaded(product: BenefitProductItem): void {
    if (!product?.id) {
      return;
    }
    this.benefitProductImageRetryAttempts.delete(product.id);
    this.setBenefitProductImageRetrying(product.id, false);
  }

  protected async assignBenefitsToTeams(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    if (!this.selectedTeamIds.size) {
      this.snackBar.open('Please select at least one team before assigning benefits.', 'Close', {
        duration: 3000,
      });
      return;
    }

    const selectedIds = this.payrollBenefitsAssignmentService.getPersistedSelectedProductIds(
      Array.from(this.selectedBenefitProductIds),
      this.companyProductIdsInOrder,
      this.getAlwaysActiveBenefitProductIds(),
    );
    const selectedTeamIds = Array.from(this.selectedTeamIds);

    this.isSaving = true;
    try {
      const metadataByTeamId = this.teams.reduce((acc: Record<string, any>, team) => {
        acc[team.id] = team.metadata || {};
        return acc;
      }, {});

      const persisted = await this.payrollBenefitsAssignmentService.assignActiveProductsToTeams(
        selectedTeamIds,
        Array.from(this.selectedBenefitProductIds),
        this.companyProductIdsInOrder,
        metadataByTeamId,
        this.getAlwaysActiveBenefitProductIds(),
        this.isNativeCompanyManager
          ? {
            managedProductIds: this.benefitProducts.map((product) => product.id),
            fallbackActiveProductIds: this.companyBaselineSelectedProductIds,
          }
          : {},
      );

      this.teams = this.teams.map(team => (
        selectedTeamIds.includes(team.id)
          ? {
            ...team,
            metadata: persisted.metadataByTeamId[team.id] || team.metadata || {},
          }
          : team
      ));

      const successMessage = selectedIds.length
        ? `Assigned ${selectedIds.length} benefits to ${selectedTeamIds.length} team(s).`
        : `Cleared all benefits for ${selectedTeamIds.length} team(s).`;
      this.clearSelections();
      this.snackBar.open(successMessage, 'Close', {
        duration: 3000,
      });
    } catch (error) {
      console.error('Unable to assign benefits to teams', error);
      this.snackBar.open('Unable to assign benefits. Please try again.', 'Close', {
        duration: 3000,
      });
    } finally {
      this.isSaving = false;
    }
  }

  protected async assignSnacksToTeams(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    if (!this.selectedTeamIds.size) {
      this.snackBar.open('Please select at least one team before assigning snacks.', 'Close', {
        duration: 3000,
      });
      return;
    }

    const selectedIds = this.payrollSnacksAssignmentService.getPersistedSelectedSnackRewardIds(
      Array.from(this.selectedSnackRewardIds),
      this.companySnackRewardIdsInOrder,
    );
    const selectedTeamIds = Array.from(this.selectedTeamIds);

    this.isSaving = true;
    try {
      const metadataByTeamId = this.teams.reduce((acc: Record<string, any>, team) => {
        acc[team.id] = team.metadata || {};
        return acc;
      }, {});

      const persisted = await this.payrollSnacksAssignmentService.assignSnackRewardsToTeams(
        selectedTeamIds,
        Array.from(this.selectedSnackRewardIds),
        this.companySnackRewardIdsInOrder,
        metadataByTeamId,
      );

      this.teams = this.teams.map(team => (
        selectedTeamIds.includes(team.id)
          ? {
            ...team,
            metadata: persisted.metadataByTeamId[team.id] || team.metadata || {},
          }
          : team
      ));

      const successMessage = selectedIds.length
        ? `Assigned ${selectedIds.length} snacks to ${selectedTeamIds.length} team(s).`
        : `Cleared all snacks for ${selectedTeamIds.length} team(s).`;
      this.clearSnackSelections();
      this.snackBar.open(successMessage, 'Close', {
        duration: 3000,
      });
    } catch (error) {
      console.error('Unable to assign snacks to teams', error);
      this.snackBar.open('Unable to assign snacks. Please try again.', 'Close', {
        duration: 3000,
      });
    } finally {
      this.isSaving = false;
    }
  }

  protected onGoToCompanyTab(): void {
    if (this.isNativeCompanyManager) {
      return;
    }
    void this.router.navigate(['payrolladmin', 'company', 'company']);
  }

  private getSelectableBenefitProductIds(): string[] {
    return this.benefitProducts
      .filter((product) => (
        product?.alwaysActive !== true
        && !this.isBenefitProductPending(product)
        && !this.isBenefitProductRequestable(product)
      ))
      .map((product) => product.id);
  }

  private getAlwaysActiveBenefitProductIds(): string[] {
    return this.payrollBenefitsAssignmentService.getAlwaysActiveBenefitProductIds(this.benefitProducts);
  }

  private async requestGroupProductOnboarding(product: BenefitProductItem): Promise<void> {
    if (!this.companyId || this.pendingOnboardingRequestProductIds.has(product.id)) {
      return;
    }

    const dialogRef = this.dialog.open<
      GroupProductOnboardingRequestDialogComponent,
      { providerName: string; planName: string },
      GroupProductOnboardingRequestDialogResult
    >(GroupProductOnboardingRequestDialogComponent, {
      width: '640px',
      maxWidth: '96vw',
      data: {
        providerName: product.vendorName || product.productName || 'Provider',
        planName: product.productName || '',
      },
      autoFocus: false,
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result?.send) {
      return;
    }

    this.pendingOnboardingRequestProductIds.add(product.id);
    try {
      await this.teamCompaniesService.submitGroupProductOnboardingRequest(
        this.companyId,
        product.id,
        result.note || '',
      );
      this.selectedBenefitProductIds.delete(product.id);
      this.selectedBenefitProductIds = new Set(this.selectedBenefitProductIds);
      this.benefitProducts = this.benefitProducts.map((item) => item.id === product.id
        ? {
          ...item,
          approvalStatus: 'pending',
          approvalLabel: 'Awaiting Wallit Confirmation',
          approvalDisabledReason:
            'Wallit has been notified and will reach out within 2 business days before this plan can be enabled.',
          canRequestApproval: false,
        }
        : item
      );
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Wallit has been notified and this product approval is now awaiting confirmation.',
      });
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: error?.error?.error || 'Unable to submit the product approval request.',
      });
    } finally {
      this.pendingOnboardingRequestProductIds.delete(product.id);
    }
  }

  private async loadData(): Promise<void> {
    this.isLoading = true;
    try {
      if (this.isNativeCompanyManager) {
        const companyState = await this.activeEmployerCompanyService.initialize();
        this.company = companyState.activeCompany;
        this.companyId = this.company?.id || '';
      }
      const providedCompanyId = `${this.companyId || ''}`.trim();
      if (providedCompanyId) {
        this.companyId = providedCompanyId;
        this.hasCompany = true;
      } else if (!this.isNativeCompanyManager) {
        const companies = await this.usersService.getTeamCompanies();
        this.company = companies?.[0];
        this.companyId = this.company?.id || '';
        this.hasCompany = !!this.companyId;
      } else {
        this.hasCompany = false;
      }

      if (!this.companyId) {
        this.hasCompany = false;
        this.benefitProducts = [];
        this.snackRewards = [];
        this.previewSnackRewardId = '';
        this.subcategoryDefinitionsByCategory = {};
        this.resetBenefitProductImageErrors();
        this.teams = [];
        this.selectedBenefitProductIds = new Set<string>();
        this.selectedSnackRewardIds = new Set<string>();
        this.selectedTeamIds = new Set<string>();
        this.companySnackRewardIdsInOrder = [];
        this.payrollProviderConnection = null;
        return;
      }

      const teamsPromise = this.teamId
        ? this.teamsService.getTeamInfo(this.teamId).then(team => (team ? [team] : []))
        : providedCompanyId
          ? this.teamsService.getTeamsForCompany(this.companyId)
          : this.teamsService.getUserTeamsInfo(this.usersService.getCurrentUserId());
      const payrollDeductionFeatureFlagPromise = this.loadPayrollDeductionFeatureFlag();
      const companySnackRewardSelectionFeatureFlagPromise = this.loadCompanySnackRewardSelectionFeatureFlag();
      const benefitsLoadPromise = this.payrollBenefitsAssignmentService.loadBenefitProductsForCompany(
        this.companyId,
        this.teamId,
        {
          includeStripeProductEnrichment: false,
          ...(this.isNativeCompanyManager
            ? {
              company: this.company,
              includeGroupProductEnrichment: false,
            }
            : {}),
        },
      );
      const payrollProviderConnectionPromise = this.payrollService
        .getCompanyPayrollProviderConnection(this.companyId)
        .catch(() => null);
      const [benefitsLoad, teams, payrollProviderConnection] = await Promise.all([
        benefitsLoadPromise,
        teamsPromise,
        payrollProviderConnectionPromise,
      ]);
      this.company = benefitsLoad.company;
      this.payrollProviderConnection = payrollProviderConnection;
      this.payrollDeductionCodes = [];
      this.payrollDeductionCodesLoadError = '';
      this.hasAttemptedPayrollDeductionCodesLoad = false;
      this.payrollDeductionCodesRequest = undefined;
      this.companyProductIdsInOrder = [...benefitsLoad.companyProductIdsInOrder];
      this.companyBaselineSelectedProductIds = [...benefitsLoad.baselineSelectedIds];
      const benefitsReadyProductIds = new Set(
        this.isNativeCompanyManager
          ? this.activeEmployerCompanyService.state.benefitsReadyProductIds
          : benefitsLoad.benefitProducts.map((product) => product.id),
      );
      this.benefitProducts = benefitsLoad.benefitProducts
        .filter((product) => benefitsReadyProductIds.has(product.id));
      this.subcategoryDefinitionsByCategory =
        this.teamCompaniesService.getResolvedPayrollConfig(this.company)?.payrollBenefitSubcategoryDefinitionsByCategory || {};
      this.resetBenefitProductImageErrors();
      this.initialProductPayrollDeductionMapSnapshot = this.serializeProductPayrollDeductionMap();
      this.selectedBenefitProductIds = new Set(
        benefitsLoad.baselineSelectedIds.filter((productId) => benefitsReadyProductIds.has(productId)),
      );
      await payrollDeductionFeatureFlagPromise;
      await companySnackRewardSelectionFeatureFlagPromise;
      await this.loadSnackRewardsIfEnabled();

      const filteredTeams = this.filterTeamsForCompany(teams, this.companyId);
      const teamsWithCounts = await Promise.all(
        filteredTeams.map(async (team: any): Promise<TeamRow> => {
          const members = await this.teamsService.getTeamMembersWithDailyLoginInfo(team.id).catch(() => []);
          return {
            id: team.id,
            name: team.name || team.id,
            membersCount: members?.length ?? 0,
            metadata: team?.metadata || {},
          };
        })
      );
      this.teams = teamsWithCounts.sort((a, b) => a.name.localeCompare(b.name));
      this.loadedActiveCompanyId = this.isNativeCompanyManager ? this.companyId : '';

      if (this.teamId) {
        this.selectedTeamIds = this.teams.some(team => team.id === this.teamId)
          ? new Set([this.teamId])
          : new Set<string>();
      } else if (this.teams.length > 0 && this.selectedTeamIds.size === 0) {
        this.selectedTeamIds = new Set([this.teams[0].id]);
      }

    } finally {
      this.isLoading = false;
    }
  }

  private resetBenefitProductImageErrors(): void {
    this.clearBenefitProductImageRetryTimers();
    this.failedBenefitProductImageIds = new Set<string>();
    this.retryingBenefitProductImageIds = new Set<string>();
    this.benefitProductImageRetryAttempts = new Map<string, number>();
  }

  private scheduleBenefitProductImageRetry(productId: string, attempt: number): void {
    const existingTimer = this.benefitProductImageRetryTimers.get(productId);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }

    const retryDelayMs = 200 * attempt;
    const retryTimer = window.setTimeout(() => {
      this.benefitProductImageRetryTimers.delete(productId);
      this.setBenefitProductImageRetrying(productId, false);
    }, retryDelayMs);
    this.benefitProductImageRetryTimers.set(productId, retryTimer);
  }

  private setBenefitProductImageRetrying(productId: string, isRetrying: boolean): void {
    const nextIds = new Set(this.retryingBenefitProductImageIds);
    if (isRetrying) {
      nextIds.add(productId);
    } else {
      nextIds.delete(productId);
    }
    this.retryingBenefitProductImageIds = nextIds;
  }

  private clearBenefitProductImageRetryTimers(): void {
    this.benefitProductImageRetryTimers.forEach((timer) => window.clearTimeout(timer));
    this.benefitProductImageRetryTimers = new Map<string, number>();
  }

  private async loadPayrollDeductionFeatureFlag(): Promise<void> {
    if (this.isNativeCompanyManager) {
      this.isPayrollDeductionFeatureEnabled = false;
      return;
    }
    if (this.payrollDeductionFeatureFlagRequest) {
      await this.payrollDeductionFeatureFlagRequest;
      return;
    }

    this.payrollDeductionFeatureFlagRequest = this.managedFeatureFlagsService.isEnabled(
      PAYROLL_ADMIN_PRODUCT_PAYROLL_DEDUCTION_FLAG,
      'team-admin',
      {
        userId: this.sessionService.getUserId() || undefined,
      },
    )
      .then((enabled) => {
        this.isPayrollDeductionFeatureEnabled = enabled;
      })
      .catch(() => {
        this.isPayrollDeductionFeatureEnabled = false;
      })
      .finally(() => {
        this.payrollDeductionFeatureFlagRequest = undefined;
      });

    await this.payrollDeductionFeatureFlagRequest;
  }

  private async loadCompanySnackRewardSelectionFeatureFlag(): Promise<void> {
    if (this.isNativeCompanyManager) {
      this.isCompanySnackRewardSelectionEnabled = true;
      return;
    }
    if (this.companySnackRewardSelectionFeatureFlagRequest) {
      await this.companySnackRewardSelectionFeatureFlagRequest;
      return;
    }

    this.companySnackRewardSelectionFeatureFlagRequest = this.managedFeatureFlagsService.isEnabled(
      PAYROLL_ADMIN_COMPANY_SNACK_REWARD_SELECTION_FLAG,
      'team-admin',
      {
        userId: this.sessionService.getUserId() || undefined,
      },
    )
      .then((enabled) => {
        this.isCompanySnackRewardSelectionEnabled = enabled;
        if (!enabled && this.activeTopTab === 'snacks') {
          this.activeTopTab = 'benefits';
        }
      })
      .catch(() => {
        this.isCompanySnackRewardSelectionEnabled = false;
        if (this.activeTopTab === 'snacks') {
          this.activeTopTab = 'benefits';
        }
      })
      .finally(() => {
        this.companySnackRewardSelectionFeatureFlagRequest = undefined;
      });

    await this.companySnackRewardSelectionFeatureFlagRequest;
  }

  private async loadSnackRewardsIfEnabled(): Promise<void> {
    if (!this.isCompanySnackRewardSelectionEnabled || !this.companyId) {
      this.snackRewards = [];
      this.previewSnackRewardId = '';
      this.companySnackRewardIdsInOrder = [];
      this.selectedSnackRewardIds = new Set<string>();
      return;
    }

    this.isLoadingSnackRewards = true;
    try {
      const snackLoad = await this.payrollSnacksAssignmentService.loadSnackRewardsForCompany(
        this.companyId,
        this.teamId,
        this.getCurrentBrand(),
        this.isNativeCompanyManager ? this.company : undefined,
      );
      this.company = {
        ...(this.company || {}),
        ...(snackLoad.company || {}),
      };
      this.snackRewards = snackLoad.snackRewards;
      this.previewSnackRewardId = '';
      this.companySnackRewardIdsInOrder = [...snackLoad.companySnackRewardIdsInOrder];
      this.selectedSnackRewardIds = new Set(snackLoad.baselineSelectedIds);
    } catch (error) {
      console.error('Unable to load snack rewards', error);
      this.snackRewards = [];
      this.previewSnackRewardId = '';
      this.companySnackRewardIdsInOrder = [];
      this.selectedSnackRewardIds = new Set<string>();
    } finally {
      this.isLoadingSnackRewards = false;
    }
  }

  private getCurrentBrand(): string {
    const me = this.usersService.me();
    return this.normalizeBrandValue(
      me?.brand?.shortName
      || me?.brand?.name
      || me?.brand?.partnerId
      || ''
    );
  }

  private normalizeBrandValue(value: any): string {
    const normalized = `${value || ''}`.trim().toLowerCase();
    if (!normalized) {
      return '';
    }

    const shortName = normalized.includes('.') ? normalized.split('.')[0] : normalized;
    return shortName.replace(/[^a-z0-9_-]/g, '');
  }

  private subscribeToActiveCompanyChanges(): void {
    if (!this.isNativeCompanyManager || this.activeCompanySubscription) {
      return;
    }
    this.activeCompanySubscription = this.activeEmployerCompanyService.stateChanges().subscribe((state) => {
      const activeCompanyId = `${state.activeCompany?.id || ''}`.trim();
      if (
        !this.currentUserReady
        || !state.initialized
        || state.loading
        || !activeCompanyId
        || activeCompanyId === this.loadedActiveCompanyId
      ) {
        return;
      }
      this.company = state.activeCompany;
      this.companyId = activeCompanyId;
      this.hasCompany = true;
      this.activeTopTab = 'benefits';
      void this.loadData();
    });
  }

  private async preloadPayrollDeductionCodesIfNeeded(): Promise<void> {
    if (!this.isPayrollDeductionAvailable) {
      return;
    }

    if (this.benefitProducts.some((product) => product.payrollDeductionEnabled)) {
      await this.ensurePayrollDeductionCodesLoaded();
    }
  }

  private async validateAndPersistPayrollDeductionConfig(): Promise<boolean> {
    if (!this.company?.id || !this.isPayrollDeductionAvailable) {
      return true;
    }

    const hasEnabledPayrollDeductionProducts = this.benefitProducts
      .some((product) => product.payrollDeductionEnabled === true);

    if (hasEnabledPayrollDeductionProducts) {
      await this.ensurePayrollDeductionCodesLoaded();
      const firstInvalidProduct = this.benefitProducts.find((product) => !!this.getPayrollDeductionValidationMessage(product));
      if (firstInvalidProduct) {
        const productLabel = firstInvalidProduct.vendorName || firstInvalidProduct.productName || 'This product';
        this.snackBar.open(`${productLabel}: ${this.getPayrollDeductionValidationMessage(firstInvalidProduct)}`, 'Close', {
          duration: 4000,
        });
        return false;
      }
    }

    const serializedMap = this.serializeProductPayrollDeductionMap();
    if (serializedMap === this.initialProductPayrollDeductionMapSnapshot) {
      return true;
    }

    const nextProductPayrollDeductionMap = JSON.parse(serializedMap);
    const updatedCompany = await this.teamCompaniesService.modifyCompanyPayrollConfig(
      this.company.id,
      (_latestPayrollConfig, latestCompany) => this.teamCompaniesService.buildProductPayrollDeductionPayrollConfig(
        latestCompany,
        nextProductPayrollDeductionMap,
      ),
    );
    const payrollConfig = updatedCompany?.payrollConfig || this.company?.payrollConfig || {};

    this.company = {
      ...(this.company || {}),
      ...(updatedCompany || {}),
      payrollConfig,
      resolvedPayrollConfig: {
        ...(this.company?.resolvedPayrollConfig || {}),
        ...(updatedCompany?.resolvedPayrollConfig || {}),
      },
    };

    if (Object.keys(nextProductPayrollDeductionMap).length > 0) {
      this.company.resolvedPayrollConfig.productPayrollDeductionMap = nextProductPayrollDeductionMap;
    } else {
      delete this.company.resolvedPayrollConfig.productPayrollDeductionMap;
    }

    this.initialProductPayrollDeductionMapSnapshot = serializedMap;
    return true;
  }

  private async ensurePayrollDeductionCodesLoaded(forceReload = false): Promise<void> {
    if (!this.company?.id || !this.isPayrollDeductionAvailable) {
      return;
    }

    if (!forceReload && this.payrollDeductionCodesRequest) {
      await this.payrollDeductionCodesRequest;
      return;
    }

    if (!forceReload && this.hasAttemptedPayrollDeductionCodesLoad) {
      return;
    }

    this.isLoadingPayrollDeductionCodes = true;
    this.hasAttemptedPayrollDeductionCodesLoad = true;
    this.payrollDeductionCodesLoadError = '';

    this.payrollDeductionCodesRequest = this.payrollService.getDeductionCodesForPayrollProviderCompany(this.company.id)
      .then((codes) => {
        this.payrollDeductionCodes = [...(codes || [])]
          .filter((code): code is UkgDeductionCode => !!`${code?.code || ''}`.trim())
          .map((code) => ({
            code: `${code.code || ''}`.trim(),
            description: `${code.description || ''}`.trim(),
            preTax: code.preTax === true,
          }))
          .sort((left, right) => {
            const leftLabel = `${left.code} ${left.description}`.trim();
            const rightLabel = `${right.code} ${right.description}`.trim();
            return leftLabel.localeCompare(rightLabel);
          });
        this.syncPayrollDeductionDescriptionsFromOptions();
      })
      .catch((error: any) => {
        this.payrollDeductionCodes = [];
        this.payrollDeductionCodesLoadError =
          error?.error?.error
          || error?.error?.message
          || error?.message
          || 'Unable to load deduction codes from UKG Ready.';
      })
      .finally(() => {
        this.isLoadingPayrollDeductionCodes = false;
        this.payrollDeductionCodesRequest = undefined;
      });

    await this.payrollDeductionCodesRequest;
  }

  private syncPayrollDeductionDescriptionsFromOptions(): void {
    this.benefitProducts = this.benefitProducts.map((product) => {
      const selectedCode = `${product?.payrollDeductionCode || ''}`.trim();
      if (!selectedCode) {
        return product;
      }

      const matchedCode = this.payrollDeductionCodes.find((option) => option.code === selectedCode);
      if (!matchedCode) {
        return product;
      }

      return {
        ...product,
        payrollDeductionCode: matchedCode.code,
        payrollDeductionCodeDescription: matchedCode.description || product.payrollDeductionCodeDescription || '',
      };
    });
  }

  private serializeProductPayrollDeductionMap(): string {
    return JSON.stringify(
      this.payrollBenefitsAssignmentService.buildProductPayrollDeductionMap(this.benefitProducts)
    );
  }

  private filterTeamsForCompany(teams: any[], companyId: string): any[] {
    if (!Array.isArray(teams)) {
      return [];
    }
    const withCompanyId = teams.filter(team => !!team?.companyId);
    if (withCompanyId.length === 0) {
      return teams;
    }
    return teams.filter(team => team?.companyId === companyId);
  }
}
